/** Shared approval-validity form controls (amount + unit; 0 = indefinite) + optional grace days. */

export type SedrApprovalValidityUnit = 'Days' | 'Months' | 'Years';

export function approvalValidityFieldHtml(options?: {
  amount?: number | null;
  unit?: string | null;
  graceDays?: number | null;
  nameAmount?: string;
  nameUnit?: string;
  nameGrace?: string;
}): string {
  const amountName = options?.nameAmount ?? 'approvalValidityAmount';
  const unitName = options?.nameUnit ?? 'approvalValidityUnit';
  const graceName = options?.nameGrace ?? 'approvalGraceDays';
  const amount = options?.amount != null && options.amount > 0 ? String(options.amount) : '0';
  const unit = (options?.unit === 'Months' || options?.unit === 'Years' || options?.unit === 'Days')
    ? options.unit
    : 'Days';
  const grace = options?.graceDays != null && options.graceDays > 0 ? String(options.graceDays) : '0';
  const showGrace = Number(amount) > 0;

  return `
    <label class="sedr-approval-validity-field">
      <span data-i18n="sedr.requirement.approvalValidity"></span>
      <div class="sedr-approval-validity-field__row">
        <input
          type="number"
          name="${amountName}"
          min="0"
          step="1"
          value="${amount}"
          inputmode="numeric"
          data-sedr-validity-amount
        />
        <select name="${unitName}" data-sedr-validity-unit>
          <option value="Days" ${unit === 'Days' ? 'selected' : ''} data-i18n="sedr.requirement.approvalValidityUnitDays"></option>
          <option value="Months" ${unit === 'Months' ? 'selected' : ''} data-i18n="sedr.requirement.approvalValidityUnitMonths"></option>
          <option value="Years" ${unit === 'Years' ? 'selected' : ''} data-i18n="sedr.requirement.approvalValidityUnitYears"></option>
        </select>
      </div>
      <span class="muted" data-i18n="sedr.requirement.approvalValidityHint"></span>
    </label>
    <label class="sedr-approval-grace-field" data-sedr-grace-field ${showGrace ? '' : 'hidden'}>
      <span data-i18n="sedr.requirement.approvalGraceDays"></span>
      <input
        type="number"
        name="${graceName}"
        min="0"
        step="1"
        value="${grace}"
        inputmode="numeric"
        data-sedr-grace-days
      />
      <span class="muted" data-i18n="sedr.requirement.approvalGraceDaysHint"></span>
    </label>
  `;
}

/** Toggle grace field visibility when validity amount changes. */
export function bindApprovalGraceVisibility(scope: ParentNode): void {
  const amountInput = scope.querySelector<HTMLInputElement>('[data-sedr-validity-amount]');
  const graceField = scope.querySelector<HTMLElement>('[data-sedr-grace-field]');
  if (!amountInput || !graceField) return;

  const sync = () => {
    const amount = Number(amountInput.value);
    const show = Number.isFinite(amount) && amount > 0;
    graceField.hidden = !show;
    if (!show) {
      const graceInput = graceField.querySelector<HTMLInputElement>('[data-sedr-grace-days]');
      if (graceInput) graceInput.value = '0';
    }
  };

  amountInput.addEventListener('input', sync);
  amountInput.addEventListener('change', sync);
  sync();
}

export function parseApprovalValidityFromForm(data: FormData): {
  approvalValidityAmount: number | null;
  approvalValidityUnit: SedrApprovalValidityUnit | null;
  clearApprovalValidity: boolean;
  approvalGraceDays: number | null;
  clearApprovalGraceDays: boolean;
} {
  const amountRaw = String(data.get('approvalValidityAmount') ?? '').trim();
  const unitRaw = String(data.get('approvalValidityUnit') ?? 'Days').trim();
  const graceRaw = String(data.get('approvalGraceDays') ?? '').trim();
  const amount = amountRaw === '' ? 0 : Number(amountRaw);
  const grace = graceRaw === '' ? 0 : Number(graceRaw);
  const unit: SedrApprovalValidityUnit =
    unitRaw === 'Months' || unitRaw === 'Years' ? unitRaw : 'Days';

  if (!Number.isFinite(amount) || amount < 0) {
    return {
      approvalValidityAmount: null,
      approvalValidityUnit: null,
      clearApprovalValidity: true,
      approvalGraceDays: null,
      clearApprovalGraceDays: true,
    };
  }

  if (amount === 0) {
    return {
      approvalValidityAmount: null,
      approvalValidityUnit: null,
      clearApprovalValidity: true,
      approvalGraceDays: null,
      clearApprovalGraceDays: true,
    };
  }

  const graceDays =
    Number.isFinite(grace) && grace > 0 ? Math.floor(grace) : null;

  return {
    approvalValidityAmount: Math.floor(amount),
    approvalValidityUnit: unit,
    clearApprovalValidity: false,
    approvalGraceDays: graceDays,
    clearApprovalGraceDays: graceDays == null,
  };
}

export function formatApprovalValidityLabel(
  amount: number | null | undefined,
  unit: string | null | undefined,
  legacyDays: number | null | undefined,
  t: (key: string, params?: Record<string, string>) => string,
): string {
  const resolvedAmount = amount ?? legacyDays ?? null;
  const resolvedUnit = unit
    ?? (legacyDays != null ? 'Days' : null);

  if (resolvedAmount == null || resolvedAmount <= 0) {
    return t('sedr.requirement.approvalValidityIndefinite');
  }

  if (resolvedUnit === 'Months') {
    return t('sedr.requirement.approvalValidityMonths', { months: String(resolvedAmount) });
  }
  if (resolvedUnit === 'Years') {
    return t('sedr.requirement.approvalValidityYears', { years: String(resolvedAmount) });
  }
  return t('sedr.requirement.approvalValidityDays', { days: String(resolvedAmount) });
}

export function formatApprovalGraceLabel(
  graceDays: number | null | undefined,
  t: (key: string, params?: Record<string, string>) => string,
): string | null {
  if (graceDays == null || graceDays <= 0) return null;
  return t('sedr.requirement.approvalGraceDaysValue', { days: String(graceDays) });
}
